<script lang="ts" setup>import { ref as _ref, computed as _computed } from 'vue';

import { reactive } from 'vue';
import { AppButton, Heading } from '@pohlcon/design-system';
import type { ISOPRO, ISOPROFireProtection } from "~/models";
import { useProductsStore } from "~/store";
import { AdaptedISOPROType, DataTableModel } from "./models";
import { deepMerge } from '~/utils/objects';

import CategoriesTable from "./CategoriesTable.vue";
import DataTable from "./ProductDataTable.vue";

const store = useProductsStore();

const data = reactive({
  selectType: 'M' as AdaptedISOPROType,
});

const types: AdaptedISOPROType[] = _ref(['M', 'D', 'Q', 'QQ', 'QZ', 'QS', 'QSZ', 'QQS', 'QQSZ', 'SM', 'SQ', 'M Variant', 'SM Variant']);

const organizedProducts: DataTableModel<ISOPRO> = _computed(() => {
  const products = store.products as ISOPRO[];
  const sortedProducts = products.reduce((t, p) => {
    if (p.type === store.adaptedProductType(data.selectType)) {
      const { fireProtectionType: FP, capacity: LBC } = p;
      const h = t?.[p.height];
      if (!h) {
        // Complete new one
        // @ts-ignore
        t[p.height] = { [FP]: { [LBC]: { [p.variant || 'none']: [p] } } };
      } else {
        // Addition
        if (!h[FP]) {
          // @ts-ignore
          h[FP] = { [LBC]: { [p.variant || 'none']: [p] } };
        } else if (!h[FP][LBC]) {
          // @ts-ignore
          h[FP][LBC] = { [p.variant || 'none']: [p] };
          // @ts-ignore
        } else if (!h[FP][LBC][(p.variant || 'none')]) {
          // @ts-ignore
          h[FP][LBC][p.variant || 'none'] = [p];
        } else {
          // @ts-ignore
          h[FP][LBC][p.variant || 'none'].push(p);
        }
      }
    }
    return t;
  }, {} as DataTableModel<ISOPRO>);

  const hasR90 = Object.values(sortedProducts).some((heightCollection) => {
    return Object.keys(heightCollection).includes('R90');
  });

  if (hasR90) {
    Object.values(sortedProducts).forEach((heightCollection) => {
      if (Object.keys(heightCollection).includes('R90') && Object.keys(heightCollection).includes('REI120')) {

        const mergedCollection = heightCollection['REI120'];
        deepMerge(mergedCollection, heightCollection['R90']);
        heightCollection['REI120/R90' as ISOPROFireProtection] = mergedCollection;

        // @ts-ignore
        delete heightCollection['R90'];
        // @ts-ignore
        delete heightCollection['REI120'];
      }
    });
  }
  return sortedProducts;
});
</script>

<template>
  <div id="data-view">
    <header>
      <Heading>Data - {{ data.selectType.toUpperCase() }} Types</Heading>
      <div id="type-selector">
        <AppButton
          v-for="type in types"
          :key="type"
          :text="type"
          :disabled="data.selectType === type"
          @click="data.selectType = type"
        />
      </div>
    </header>

    <section class="type-section">
      <DataTable
        :model="organizedProducts"
        :selectedType="data.selectType"
      />
    </section>

    <hr />
    <Heading :level="2">Overview thermal categories of installation setups</Heading>

    <section class="category-section">
      <CategoriesTable
        class="categories-table"
        :productType="data.selectType"
      />
    </section>
  </div>
</template>

<style lang="scss">
@use '../../variables' as *;

@media screen and (min-width: $breakpoint-desktop) {
  #app__wrapper {
    width: 90vw;
  }
}

#data-view {
  display: grid;
  grid-template-columns: 1fr;

  &>header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--gutter-m);
    margin-bottom: var(--gutter-l);
  }

  #type-selector {
    display: flex;
    gap: var(--gutter-s);
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
</style>
